import type { Control, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { FormFieldInput } from '@/shared/components/custom/form-field/FormFieldInput';
import { FormFieldTextarea } from '@/shared/components/custom/form-field/FormFieldTextarea';
import { FormFieldDatePicker } from '@/shared/components/custom/form-field/FormFieldDatePicker';
import { ColorPickerPopover } from '@/shared/components/custom/color-picker/color-picker.index';
import { getPaletteColor } from '@/core/lib/colors';
import type { CreateProjectInput } from '../schemas';

interface ProjectFormFieldsProps {
  control: Control<CreateProjectInput>;
  setValue: UseFormSetValue<CreateProjectInput>;
  selectedColor: string | undefined;
  isPending: boolean;
}

/** Field markup shared by `ProjectFormDialog` (edit mode) and `CreateProjectDialog`'s inline "blank project" tab. No `<form>` tag — the caller owns that. */
export function ProjectFormFields({
  control,
  setValue,
  selectedColor,
  isPending,
}: ProjectFormFieldsProps) {
  const { t } = useTranslation('projects');
  const colorValue = selectedColor ? getPaletteColor(selectedColor)?.value : undefined;

  return (
    <>
      <div className="flex items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-foreground">{t('dialog.color')}</label>
          <ColorPickerPopover
            value={selectedColor ?? ''}
            onSelect={(color) => setValue('color', color, { shouldValidate: true })}
            trigger={
              <Button
                type="button"
                variant="outline"
                className="h-10 w-10 p-0"
                disabled={isPending}
                data-testid="projects-color-picker"
              >
                {colorValue && (
                  <div className="h-6 w-6 rounded-full" style={{ background: colorValue }} />
                )}
              </Button>
            }
          />
        </div>

        <FormFieldInput
          control={control}
          name="name"
          label={t('dialog.name')}
          floatingLabel
          disabled={isPending}
          data-testid="projects-name-input"
        />
      </div>

      <FormFieldTextarea
        control={control}
        name="description"
        label={t('dialog.description')}
        floatingLabel
        rows={3}
        disabled={isPending}
        data-testid="projects-description-input"
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FormFieldDatePicker
          control={control}
          name="startDate"
          label={t('dialog.startDate')}
          floatingLabel
          disabled={isPending}
          data-testid="projects-startDate-input"
        />
        <FormFieldDatePicker
          control={control}
          name="endDate"
          label={t('dialog.endDate')}
          floatingLabel
          disabled={isPending}
          data-testid="projects-endDate-input"
        />
      </div>
    </>
  );
}
